import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSave } from "react-icons/fa";
import { Button } from 'components/common';
import UnderlineInput from "../common/UnderlineInput";
import { updateDigitalSignatureConfig, testDigitalSignature } from "../common/settingsApi";
import { TH, TD, ROW, TONE, Chip, SettingsCard, GroupRow, Toggle } from "./settingsKit";

const COLS = 3;

// Bootstrap icon cho leftIcon của Button (bỏ qua `size` — là font icon).
const PlugIcon = () => <i className="bi bi-plug" />;

// Một dòng = một thiết lập. `kind`: text | secret | toggle.
const HUB_ROWS = [
  { key: "HUB_SIGN_URL", label: "Server URL", kind: "text",
    placeholder: "https://sign-hub.pebsteel.com.vn" },
  { key: "HUB_SIGN_API_KEY", label: "API key", kind: "secret", placeholder: "hsak_...",
    hint: "Optional — empty uses the key HR Hub issues to Portal.", fallback: "Using HR Hub key" },
  { key: "HUB_SIGN_MAGIC_SECRET", label: "Sign-by-email secret", kind: "secret",
    placeholder: "Shared secret", hint: "Needed for “approve from email” links. Must match Hub-Sign." },
];
const SCA_ROWS = [
  { key: "SMARTCA_ENABLED", label: "Use SmartCA", kind: "toggle",
    hint: "On = Head of Finance and BOD sign each approval with their SmartCA certificate (every signer needs a ready certificate below). Off = no certificate signature." },
  { key: "SMARTCA_API_URL", label: "API URL", kind: "text",
    placeholder: "https://.../sca/sp769", hint: "HTTPS, including the /sca/sp769 path." },
  { key: "SMARTCA_CLIENT_ID", label: "Client ID", kind: "text", placeholder: "sp_id from VNPT" },
  { key: "SMARTCA_CLIENT_SECRET", label: "Client secret", kind: "secret", placeholder: "sp_password from VNPT" },
  { key: "SMARTCA_PORTAL_ENABLED", label: "Sign on Portal", kind: "toggle",
    hint: "Approver enters SmartCA password + OTP on Portal. Off = confirm in the VNPT SmartCA app." },
];
const ALL_KEYS = [...HUB_ROWS, ...SCA_ROWS].map((r) => r.key);

const MISSING_LABELS = { SMARTCA_API_URL: "API URL", SMARTCA_CLIENT_ID: "Client ID",
  SMARTCA_CLIENT_SECRET: "Client secret", SMARTCA_ENABLED: "Use SmartCA (switch on)" };
const missingLabel = (key) => MISSING_LABELS[key] || key.replace(/^SMARTCA_/, "").replace(/_/g, " ");

const formValue = (m) => (m?.secret ? "" : (m?.value || ""));

/** Trạng thái của một thiết lập theo giá trị ĐÃ LƯU. */
const statusOf = (row, m, dirty) => {
  if (dirty) return <Chip tone={TONE.muted}>Unsaved</Chip>;
  if (row.kind === "toggle") return m?.value === "true" ? <Chip tone={TONE.ok}>On</Chip> : <Chip tone={TONE.muted}>Off</Chip>;
  if (row.kind === "secret") {
    if (m?.set) return <Chip tone={TONE.ok} icon="bi-check2">Configured</Chip>;
    return row.fallback ? <Chip tone={TONE.muted}>{row.fallback}</Chip> : <Chip tone={TONE.warn}>Not set</Chip>;
  }
  if (!m?.value) return <Chip tone={TONE.warn}>Not set</Chip>;
  return m.source === "env" ? <Chip tone={TONE.muted}>From .env</Chip> : <Chip tone={TONE.ok} icon="bi-check2">Set</Chip>;
};

/**
 * Chữ ký số của Payment Request (admin) — cùng khuôn với tab Signers: một card bảng,
 * mỗi dòng một thiết lập, nhóm theo dịch vụ.
 *   - Hub-Sign: ảnh chữ ký in lên phiếu khi ký (mỗi người tự tạo ở My signature).
 *   - VNPT SmartCA: ký số bằng chứng thư (chưa gắn vào luồng duyệt — client mới dừng ở
 *     tầng transport, xem External::SmartCa::Client).
 *
 * Giá trị lưu ở App Settings (cùng chỗ trang /settings ghi). Secret không bao giờ được
 * trả về trình duyệt; để trống = giữ nguyên.
 *
 * `data` = GET digital_signature (DigitalSignatureTab tải một lần cho cả tab);
 * `onSaved(data)` báo cho tab để bảng chứng thư bên dưới cập nhật theo.
 */
const DigitalSignaturePanel = ({ data, onSaved }) => {
  const [meta, setMeta] = useState({});     // key -> { secret, set, source, value, fallback }
  const [form, setForm] = useState({});     // key -> giá trị đang nhập
  const [smartCa, setSmartCa] = useState(null);
  const loading = !data;
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  const apply = (data) => {
    const settings = data?.settings || {};
    setMeta(settings);
    setSmartCa(data?.smart_ca || null);
    setForm(Object.fromEntries(Object.keys(settings).map((k) => [k, formValue(settings[k])])));
  };

  useEffect(() => { if (data) apply(data); }, [data]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const isDirty = (key) => (form[key] ?? "") !== formValue(meta[key]);
  const changed = ALL_KEYS.filter(isDirty);
  const hubDirty = HUB_ROWS.some((r) => isDirty(r.key));

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateDigitalSignatureConfig(Object.fromEntries(changed.map((k) => [k, form[k]])));
      apply(res.data);
      onSaved?.(res.data);
      if (hubDirty) setTest(null);
      toast.success("Digital signature settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      setTest((await testDigitalSignature()).data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  const hub = test?.hub_sign;
  const hubStatus = !hub ? <Chip tone={TONE.muted}>Not tested</Chip>
    : !hub.ok ? <Chip tone={TONE.bad} icon="bi-x-circle-fill">Cannot connect</Chip>
      : <Chip tone={TONE.ok} icon="bi-check-circle-fill">Connected</Chip>;
  const scaStatus = smartCa?.configured ? <Chip tone={TONE.ok} icon="bi-check-circle-fill">SmartCA ready</Chip>
    : meta.SMARTCA_ENABLED?.value === "true" ? <Chip tone={TONE.warn} icon="bi-exclamation-circle">SmartCA incomplete</Chip>
      : <Chip tone={TONE.muted} icon="bi-pause-circle">SmartCA off</Chip>;
  const scaOff = form.SMARTCA_ENABLED !== "true";

  const renderRow = (row) => {
    const m = meta[row.key];
    const dirty = isDirty(row.key);
    return (
      <tr key={row.key} className={`${ROW} ${dirty ? "tw-bg-geist-accent/5" : ""}`}>
        <td className={`${TD} tw-w-[32%] tw-min-w-[220px]`}>
          <label htmlFor={`ds-${row.key}`} className="tw-text-sm tw-font-semibold tw-text-geist-fg">
            {row.label}
          </label>
          {row.hint && <div className="tw-text-[12px] tw-text-geist-fg-3 tw-leading-snug tw-mt-0.5">{row.hint}</div>}
        </td>
        <td className={`${TD} tw-min-w-[280px]`}>
          {row.kind === "toggle" ? (
            <Toggle checked={form[row.key] === "true"} label={row.label}
              onChange={(v) => set(row.key)(v ? "true" : "false")} />
          ) : (
            <UnderlineInput id={`ds-${row.key}`} type={row.kind === "secret" ? "password" : "text"} autoComplete="off"
              value={form[row.key] || ""} onChange={(e) => set(row.key)(e.target.value)}
              placeholder={row.kind === "secret" && m?.set ? "•••••••• — leave empty to keep" : row.placeholder} />
          )}
        </td>
        <td className={`${TD} tw-w-36`}>{statusOf(row, m, dirty)}</td>
      </tr>
    );
  };

  return (
    <SettingsCard
      title="Digital signature"
      subtitle="Where signatures come from when someone approves. Secrets are never shown — leave empty to keep the current value."
      status={!loading && <>{hubStatus}{scaStatus}</>}
      actions={(
        <>
          <Button variant="secondary" size="sm" leftIcon={PlugIcon} disabled={loading || testing || hubDirty} onClick={runTest}
            title={hubDirty ? "Save first, then test" : "Read your own signature from Hub-Sign (read-only)"}>
            {testing ? "Testing..." : "Test Hub-Sign"}
          </Button>
          <Button variant="primary" size="sm" leftIcon={FaSave} disabled={loading || saving || changed.length === 0} onClick={save}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      )}
      footer={!loading && (
        <p className="tw-text-[11px] tw-text-geist-fg-3 tw-m-0 tw-max-w-3xl">
          Hub-Sign supplies the signature image. SmartCA signs the PDF with each person's certificate — set up who signs in “Signer certificates” below.
        </p>
      )}
    >
      {loading ? (
        <div className="tw-py-10 tw-text-center tw-text-geist-fg-3 tw-text-sm">Loading...</div>
      ) : (
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-border-collapse">
            <thead>
              <tr className={`tw-bg-geist-bg-2 ${ROW}`}>
                <th className={TH}>Setting</th>
                <th className={TH}>Value</th>
                <th className={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              <GroupRow colSpan={COLS} icon="bi-vector-pen" title="Hub-Sign"
                subtitle="signature image stamped on the printed form" />
              {hub && (
                <tr className={`${ROW} ${hub.ok ? "tw-bg-[#f6fef9]" : "tw-bg-[#fdf2f0]"}`}>
                  <td className={`${TD} tw-text-sm tw-font-semibold tw-text-geist-fg`}>Test result</td>
                  <td className={TD}>
                    <div className="tw-flex tw-items-center tw-gap-3">
                      {hub.signature_url && (
                        <span className="tw-inline-flex tw-h-10 tw-w-32 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-white">
                          <img src={hub.signature_url} alt="Your signature" className="tw-max-h-8 tw-max-w-[112px] tw-object-contain" />
                        </span>
                      )}
                      <span className="tw-text-[13px] tw-text-geist-fg-2">{hub.message}</span>
                    </div>
                  </td>
                  <td className={TD}>{hubStatus}</td>
                </tr>
              )}
              {HUB_ROWS.map((r) => renderRow(r))}

              <GroupRow colSpan={COLS} icon="bi-shield-lock" title="VNPT SmartCA"
                subtitle="certificate signing on the PDF" />
              {SCA_ROWS.map((r) => renderRow(r))}
              {smartCa && !smartCa.configured && (
                <tr className={ROW}>
                  <td className={`${TD} tw-text-[12px] tw-text-geist-fg-3`} colSpan={COLS}>
                    <i className="bi bi-info-circle tw-mr-1" />
                    {scaOff
                      ? "SmartCA is off. Fill in the fields, switch “Use SmartCA” on, then Save."
                      : `Still missing: ${(smartCa.missing || []).map(missingLabel).join(", ")}.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </SettingsCard>
  );
};

DigitalSignaturePanel.propTypes = {
  // GET digital_signature payload; null while the tab is still loading.
  data: PropTypes.shape({
    settings: PropTypes.objectOf(PropTypes.shape({
      secret: PropTypes.bool,
      set: PropTypes.bool,
      source: PropTypes.string,
      value: PropTypes.string,
    })),
    smart_ca: PropTypes.object,
  }),
  onSaved: PropTypes.func,
};

export default DigitalSignaturePanel;
